import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatPostTime } from "../utils/formatPostTime";
import UserInfoOverlay from "../components/UserInfoOverlay";

export default function Notifications({
  notificationCounts,
  users = [],
  onBack,
  myChatId,
  currentUser,
  groups = [],
  groupMembers = {},
  socket,
  uploadImage,
}) {
  const navigate = useNavigate();
  const [selectedUserId, setSelectedUserId] = useState(null);

  const notifications = useMemo(() => {
    if (!Array.isArray(notificationCounts)) return [];
    return [...notificationCounts]
      .filter((item) =>
        users.find((u) => String(u.chatId) === String(item.actorChatId))
      )
      .sort(
        (a, b) =>
          new Date(b.lastActivityAt || 0) - new Date(a.lastActivityAt || 0)
      );
  }, [notificationCounts, users]);

 const resolveActor = (chatId) => {
  const found = users.find(
    (u) => String(u.chatId) === String(chatId)
  );
  if (!found) return null;

  return {
    displayName: found.displayName || found.username || "Unknown",
    username: found.username?.replace(/^@/, "") || String(chatId),
    profileImageUrl: found.profileImageUrl || null,
  };
};

  const resolveActionText = (item) => {
    switch (item.type) {
      case "post_like":      return "liked your post";
      case "post_repost":    return "reposted your post";
      case "post_quote":     return "quoted your post";
      case "post_comment":   return "commented on your post";
      case "comment_like":   return "liked your comment";
      case "comment_repost": return "reposted your comment";
      case "comment_quote":  return "quoted your comment";
          case "post_support":
        return `supported your post with ${formatUswd(item.amount)} USWD`;
      case "comment_support":
        return `supported your comment with ${formatUswd(item.amount)} USWD`;
      default:               return "";
    }
  };

  const formatUswd = (amount) => {
    const n = Number(amount);
    if (!n) return "0";
    return n % 1 === 0 ? String(n) : n.toFixed(2);
  };

  const handleOpenPost = (item) => {
    navigate(".", {
      state: {
        view: "post",
        post: { postId: item.postId },
        previewCommentId: item.commentId || null,
      },
    });
  };

  const selectedUser = users.find(
    (u) => String(u.chatId) === String(selectedUserId)
  );

  const handleBack = () => {
  // Keep mobile behavior.
  onBack?.();

  // Also clear the route state so desktop returns to Feed.
  navigate(".", {
    replace: true,
    state: null,
  });
};

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-xl mx-auto">
        <div
          className="flex items-center gap-3 px-4 pb-2 md:px-0"
          style={{ paddingTop: "max(16px, env(safe-area-inset-top, 16px))" }}
        >
          <button
            onClick={onBack}
            className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 touch-manipulation transition-colors"
            aria-label="Back"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>
          <h2 className="text-xl font-semibold">Notifications</h2>
        </div>
        <div
          className="p-4 pt-0 space-y-3"
          style={{
            paddingBottom: "calc(var(--bottom-tabs-h, 56px) + env(safe-area-inset-bottom, 0px) + 16px)",
          }}
        >
          {notifications.length === 0 && (
            <div className="text-sm opacity-60">No notifications</div>
          )}
          {notifications.map((item, idx) => {
            const actor = resolveActor(item.actorChatId);
            // actor will always exist here because we filtered above,
            // but guard just in case
            if (!actor) return null;
            const isSupport = item.type === "post_support" || item.type === "comment_support";
            return (
              <div
                key={`${item.postId}-${item.commentId || ""}-${item.type}-${idx}`}
                onClick={() => handleOpenPost(item)}
                className="cursor-pointer p-4 rounded-xl bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition"
              >
                <div className="flex items-start gap-3">
                  {actor.profileImageUrl ? (
                    <img
                      src={actor.profileImageUrl}
                      className="w-10 h-10 rounded-full object-cover cursor-pointer"
                      alt=""
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedUserId(item.actorChatId);
                      }}
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full bg-neutral-400 flex items-center justify-center text-white cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedUserId(item.actorChatId);
                      }}
                    >
                      👤
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                     <div className="text-sm font-medium">
    <span
      className="cursor-pointer hover:underline"
      onClick={(e) => {
        e.stopPropagation();
        setSelectedUserId(item.actorChatId);
      }}
    >
      {actor.displayName}
    </span>{" "}

    <span
      className="cursor-pointer hover:underline text-neutral-500"
      onClick={(e) => {
        e.stopPropagation();
        setSelectedUserId(item.actorChatId);
      }}
    >
      @{actor.username}
    </span>{" "}

    {resolveActionText(item)}
    {isSupport && <span className="ml-1">💸</span>}
  </div>
                      <div className="text-xs text-neutral-400 ml-3 whitespace-nowrap">
                        {formatPostTime(item.lastActivityAt, "relative")}
                      </div>
                    </div>
                    {item.previewText && (
                      <div className="mt-2 text-sm text-neutral-500 line-clamp-2">
                        {item.previewText}
                      </div>
                    )}
                    {item.unreadCount > 0 && (
                      <div className="mt-2 text-xs text-blue-500 font-medium">
                        {item.unreadCount} new
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <UserInfoOverlay
        open={!!selectedUserId}
        onClose={() => setSelectedUserId(null)}
        user={selectedUser}
        mutualGroups={
          (groups || []).filter((g) => {
            const mem = groupMembers?.[g.groupId] || g.members || [];
            return (
              Array.isArray(mem) &&
              mem.some((m) => String(m.chatId) === String(myChatId)) &&
              mem.some((m) => String(m.chatId) === String(selectedUserId))
            );
          })
        }
        uploadImage={uploadImage}
        socket={socket}
        myChatId={myChatId}
        isFollowing={selectedUser?.isFollowing}
        onFollow={() => socket?.followUser?.({ targetChatId: selectedUserId })}
        onUnfollow={() => socket?.unfollowUser?.({ targetChatId: selectedUserId })}
        followersCount={selectedUser?.followersCount || 0}
        followingCount={selectedUser?.followingCount || 0}
      />
    </div>
  );
}